import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { checkGuestInteractions } from '../api/medications';
import { pollInteractionCheck } from '../utils/interactionChecker';
import {
  createEnhancedInteractionData,
  groupInteractionsBySupplementKey,
} from '../utils/interactionUtils';
import { getMedicationDisplayName } from '../utils/medicationUtils';
import { asDisplayMedications } from '../utils/guestMedications';
import { logAnalyticsEvent } from '../utils/analytics';

// The interaction check for a logged-out visitor's generated stack (STACK-565).
//
// Design: docs/guest-medication-check.md §3 steps 3 and 5, §3.2, §5. The
// server finds the stack itself from the goal text, so the request carries
// only `{ healthGoals, medications: [in_rxcui, …] }` — never supplements,
// never a name. The result comes back in the members' shape and is turned
// into the same per-supplement `interaction` objects the flag UI already
// renders, named from the tab store by `in_rxcui`.
//
// Results stay in this hook's state. They are never written into the stack's
// supplements, so nothing a visitor's check found can reach the
// `unsavedStack_<id>` snapshot in localStorage (§3.1).

export const GUEST_CHECK_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  FLAGS: 'flags',
  CLEAN: 'clean',
  ERROR: 'couldnt_check',
};

/**
 * The analytics `reason` for a refusal (§3.2). Every one renders the same
 * couldn't-check state; the reason only tells us which refusal it was.
 */
export const refusalReason = (error) => {
  if (error?.timedOut) return 'timeout';
  switch (error?.status) {
    case 400: return 'bad_request';
    case 409: return 'stack_not_found';
    case 429: return 'rate_limited';
    case 503:
      if (error.code === 'guest_ai_budget_spent') return 'budget_spent';
      if (error.code === 'name_lookup_failed') return 'name_lookup_failed';
      return 'server_error';
    default: return 'server_error';
  }
};

const keyOf = (s) => s?.supplementId || s?.key;

/**
 * Every supplement × medication pair this check asked about, with the risk
 * found: `high`, `medium` or `low`, or `none` for a pair the server did not
 * list (it lists only pairs it found an interaction for). A listed pair with
 * no risk is flagged on screen, so it is `unknown` here, never `none`. The
 * `pairs` of the result event (§5, STACK-580); never reasoning text. Same
 * shape as the interaction checker page's.
 */
export const pairsForAnalytics = (interactions, supplementKeys, rxcuis) =>
  supplementKeys.flatMap((key) => rxcuis.map((rxcui) => {
    const hit = (interactions || []).find((i) => i?.supplement_key === key
      && String(i.medication_in_rxcui) === String(rxcui));
    return {
      supplement_key: key,
      in_rxcui: rxcui,
      risk: hit ? String(hit.risk || 'unknown').toLowerCase() : 'none',
    };
  }));

/**
 * @param {Object|null} guestCheck - `{ healthGoals, medications, generatedKeys }`
 *   for a logged-out visitor with medications, else null. `healthGoals` is the
 *   exact string sent to /v2/generate-stack; `generatedKeys` the supplement
 *   keys the generator produced.
 * @param {Array} supplements - the stack's rows as shown now
 * @param {Object} [options] - `{ retryDelayMs, maxRetries }`, for tests
 */
export default function useGuestInteractionCheck(guestCheck, supplements, options = {}) {
  const healthGoals = guestCheck?.healthGoals || '';
  const medications = useMemo(() => guestCheck?.medications || [], [guestCheck]);
  const generatedKeys = useMemo(() => guestCheck?.generatedKeys || [], [guestCheck]);
  const active = Boolean(healthGoals && medications.length && generatedKeys.length);

  const [status, setStatus] = useState(GUEST_CHECK_STATUS.IDLE);
  const [reason, setReason] = useState(null);
  const [interactionsByKey, setInteractionsByKey] = useState({});
  const [attempt, setAttempt] = useState(0);
  const runRef = useRef(0);
  const { retryDelayMs, maxRetries } = options;

  const rxcuis = useMemo(() => medications.map((m) => m.in_rxcui), [medications]);
  // A stable identity for "this check": a new goal, stack or medication list
  // is a new check; re-renders are not.
  const signature = active
    ? `${healthGoals}\u0000${[...generatedKeys].sort().join('|')}\u0000${rxcuis.join(',')}`
    : '';

  useEffect(() => {
    if (!signature) {
      setStatus(GUEST_CHECK_STATUS.IDLE);
      setReason(null);
      setInteractionsByKey({});
      return undefined;
    }
    const run = ++runRef.current;
    const current = () => run === runRef.current;
    const displayMeds = asDisplayMedications(medications);
    const nameOf = (rxcui) => getMedicationDisplayName(rxcui, displayMeds);
    // Every request counts against the per-IP ceiling, so a check no longer
    // wanted stops asking at once (STACK-575).
    const controller = new AbortController();

    setStatus(GUEST_CHECK_STATUS.CHECKING);
    setReason(null);
    setInteractionsByKey({});

    pollInteractionCheck(() => checkGuestInteractions(healthGoals, rxcuis), {
      ...(retryDelayMs !== undefined && { retryDelayMs }),
      ...(maxRetries !== undefined && { maxRetries }),
      signal: controller.signal,
    })
      .then((response) => {
        if (!current()) return;
        const grouped = groupInteractionsBySupplementKey(response?.interactions || []);
        const byKey = {};
        generatedKeys.forEach((key) => {
          byKey[key] = createEnhancedInteractionData(grouped[key] || [], nameOf);
        });
        const flagCount = Object.values(byKey).filter((i) => i.has_interaction).length;
        setInteractionsByKey(byKey);
        setStatus(flagCount ? GUEST_CHECK_STATUS.FLAGS : GUEST_CHECK_STATUS.CLEAN);
        // The stack's supplements, the medications' RxCUIs and every pair's
        // risk, clean pairs included; never a name or reasoning text (§5,
        // STACK-580). Mixpanel only: `logAnalyticsEvent` never reaches Google.
        logAnalyticsEvent('guest_interaction_check_result', {
          outcome: flagCount ? 'flags' : 'clean',
          flag_count: flagCount,
          medication_count: rxcuis.length,
          supplement_count: generatedKeys.length,
          medications: rxcuis,
          supplements: generatedKeys,
          pairs: pairsForAnalytics(response?.interactions, generatedKeys, rxcuis),
        });
      })
      .catch((error) => {
        if (!current()) return;
        const why = refusalReason(error);
        setInteractionsByKey({});
        setReason(why);
        setStatus(GUEST_CHECK_STATUS.ERROR);
        // What it was for, so a refusal can be traced to its medications.
        logAnalyticsEvent('guest_interaction_check_result', {
          outcome: 'couldnt_check',
          reason: why,
          flag_count: 0,
          medication_count: rxcuis.length,
          supplement_count: generatedKeys.length,
          medications: rxcuis,
          supplements: generatedKeys,
          pairs: [],
        });
      });

    return () => {
      // A newer check, or unmount: this run's answer is no longer wanted.
      if (current()) runRef.current += 1;
      controller.abort();
    };
    // `signature` captures goals, keys and medications; `attempt` is Try again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const generatedSet = useMemo(() => new Set(generatedKeys), [generatedKeys]);

  /** The row with this check's `interaction` attached, for the flag UI. */
  const decorate = useCallback((supplement) => {
    if (!active) return supplement;
    const found = interactionsByKey[keyOf(supplement)];
    return found ? { ...supplement, interaction: found } : supplement;
  }, [active, interactionsByKey]);

  /**
   * The row's placeholder chip: 'checking' while the check runs, 'unchecked'
   * for a supplement added after generation (never checked), else null.
   */
  const rowChip = useCallback((supplement) => {
    if (!active) return null;
    if (!generatedSet.has(keyOf(supplement))) return 'unchecked';
    if (status === GUEST_CHECK_STATUS.CHECKING) return 'checking';
    return null;
  }, [active, generatedSet, status]);

  const flagCount = (supplements || []).filter(
    (s) => generatedSet.has(keyOf(s)) && interactionsByKey[keyOf(s)]?.has_interaction
  ).length;

  return {
    active,
    status,
    reason,
    medications,
    flagCount,
    checkedCount: generatedKeys.length,
    decorate,
    rowChip,
    retry,
  };
}
